"use client"
import { FC } from 'react';
import Image from 'next/image';
import { Content } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import { SwiperSlide } from 'swiper/react';
import ScrollBarSwiper from '../../../app/components/scrollbar-swiper';
import s from './attribute-swiper.module.css'
import cn from 'clsx';

/**
 * Props for `AttributeSwiper`.
 */
export type AttributeSwiperProps =
    SliceComponentProps<Content.AttributeSwiperSlice>;

/**
 * Component for "AttributeSwiper" Slices.
 */
const AttributeSwiper: FC<AttributeSwiperProps> = ({ slice, context }) => {
    const { theme } = (context || {}) as { theme?: string };
    const attributes = slice.primary.attributes ?? [];
    const isDarkTheme = theme?.includes('dark') ?? false;
    return (
        <section
            className={s.attributeSwiper}
            data-slice-type={slice.slice_type}
            data-slice-variation={slice.variation}
        >
            <div className={cn(s.attributeSwiperMobile, isDarkTheme && s.darkTheme)}>
                {attributes.map((attribute, index) => 
                    <div key={`${attribute.title}-${index}`} className={s.attribute}>
                        <div className={s.attributeImageContainer}>
                            <Image 
                                src={attribute.icon.url}
                                width={48} 
                                height={48}  
                                alt={attribute.icon.alt}
                            />
                        </div>
                        <div className={s.stack}>
                            <p className="mmds-component-two-detail">{attribute.title}</p>
                            <p className="mmds-component-two-detail">{attribute.rating}</p>
                            <p className="mmds-component-two-detail">{attribute.text}</p>
                        </div>
                    </div>
                )}
            </div>
            <div className={s.attributeSwiperDesktop}>
                <ScrollBarSwiper
                    className='attribute-swiper-desktop'
                    swiperIconNextClassName="swiper-icon-next-10"
                    swiperIconPrevClassName="swiper-icon-prev-10"
                    spaceBetweenDesktop={12}
                    spaceBetweenMobile={8}
                    slidePerViewInMobile={1.2}
                    slidePerViewInDesktop="auto"
                    hasScrollbar={false}
                >
                    {attributes.map((attribute, index) => 
                        <SwiperSlide
                            key={`${attribute.title}-${index}`}
                            data-testid={`${attribute.title}-attribute`}
                            className={s.swiperSlide}
                        >
                            <div className={s.attribute}>
                                <div className={s.attributeImageContainer}>
                                    <Image 
                                        src={attribute.icon.url}
                                        width={64} 
                                        height={64}  
                                        alt={attribute.icon.alt}
                                    />
                                </div>
                                <div className={s.stack}>
                                    <p className="mmds-copy-three-detail">{attribute.title}</p>
                                    <p className="mmds-copy-three-detail">{attribute.rating}</p>
                                    <p className="mmds-copy-three-detail">{attribute.text}</p>
                                </div>
                            </div>
                        </SwiperSlide>
                    )}
                </ScrollBarSwiper>
            </div>
        </section>
    );
};

export default AttributeSwiper;
